import type { SharedProps } from '@bonniernews/dn-design-system-web/assets/types/shared-props.ts'
import type {
  TeaserBaseProps,
  TeaserFooterProps,
  TeaserHighlightProps,
  TeaserItalicHeadlineProps,
  TeaserMediaProps,
  TeaserThemeProps,
  TeaserVignetteProps,
} from '@bonniernews/dn-design-system-web/assets/types/teaser-props.ts'

import { formatClassString } from '@bonniernews/dn-design-system-web/helpers/formatClassString.ts'

import { Button } from '@bonniernews/dn-design-system-web/components/button/button.tsx'
import { GroupHeader } from '@bonniernews/dn-design-system-web/components/group-header/group-header.tsx'
import { IconUse } from '@bonniernews/dn-design-system-web/components/icon-sprite/icon-sprite.tsx'
import { TeaserFooter } from '@bonniernews/dn-design-system-web/components/teaser-footer/teaser-footer.tsx'

interface TeaserListVerticalProps extends SharedProps {
  /** List title */
  listTitle?: string
  /** Ex. { text: "Alla nyheter", url: "#" } */
  listLink?: ListLink
  /** If true, the list will be flexible */
  flexible?: boolean
  /** Array with teaser objects. Ex. [ { title: 'Malmö var det enda rimliga valet för Eurovision', targetLink: '#', highlight: 'Hanna Fahl:', publicationTime: 'I GÅR 12:28', sectionName: 'Kultur', attributes: { "data-test": "list-item-test-data" }, showPlayButton: false } ] */
  teasers: Array<ListTeaser>
  /** The theme-class to apply to _all_ teasers in the list. */
  theme?: 'nyheter' | 'kultur'
}

interface ListLink extends SharedProps {
  text: string
  url: string
}

interface ListTeaser
  extends TeaserBaseProps,
    TeaserThemeProps,
    TeaserMediaProps,
    TeaserVignetteProps,
    TeaserHighlightProps,
    TeaserItalicHeadlineProps,
    TeaserFooterProps {
  showPlayButton?: boolean
  variant?: 'default' | 'podcast'
}

/**
 * - GitHub: [BonnierNews/dn-design-system/web/src/components/teaser-list-vertical](https://github.com/BonnierNews/dn-design-system/tree/main/web/src/components/teaser-list-vertical)
 * - Storybook: [TeaserListVertical](https://designsystem.dn.se/?path=/docs/section-teaserlistvertical--docs)
 *
 * The component will not include styling by itself. Make sure to include the right styles for the component. See example below:
 * `@use '@bonniernews/dn-design-system-web/components/teaser-list-vertical/teaser-list-vertical.scss'`
 */
export const TeaserListVertical = ({
  listTitle,
  listLink,
  flexible = false,
  teasers,
  theme,
  classNames,
  attributes = {},
}: TeaserListVerticalProps) => {
  const componentClassName = 'ds-teaser-list-vertical'
  const showButton = listLink && listLink.text && listLink.url

  const classes = formatClassString([
    componentClassName,
    theme && `ds-theme--${theme}`,
    flexible && `${componentClassName}--flexible`,
    showButton && `${componentClassName}--linked`,
    classNames,
  ])

  return (
    <div className={classes} {...attributes}>
      {listTitle && <GroupHeader title={listTitle} showBottomBorder={flexible} />}
      <div className={`${componentClassName}__content`}>
        {teasers.map((teaser, index) => (
          <TeaserListVerticalItem key={index} {...teaser} />
        ))}
      </div>
      {showButton && (
        <div className={`${componentClassName}__link`}>
          <Button
            text={listLink.text}
            href={listLink.url}
            size='lg'
            variant='secondaryFilled'
            fullWidth={true}
            classNames={listLink.classNames}
            attributes={listLink.attributes as { [key: string]: string }}
            forcePx={false}
          />
        </div>
      )}
    </div>
  )
}

function TeaserListVerticalItem({
  title,
  vignette,
  targetLink,
  highlight,
  publicationTime,
  sectionName,
  showPlayButton,
  isItalicHeadline,
  theme,
  media,
  classNames,
  attributes = {},
}: ListTeaser) {
  const componentClassName = 'ds-teaser-list-vertical__item'

  const classes = formatClassString([
    componentClassName,
    isItalicHeadline && `${componentClassName}--italic`,
    theme && `ds-theme--${theme}`,
    classNames,
  ])

  return (
    <a href={targetLink} className={classes} {...attributes}>
      <div className={`${componentClassName}-text`}>
        {vignette && <div className={`${componentClassName}-vignette`}>{vignette}</div>}
        <h3 className={`${componentClassName}-title`}>
          {highlight && <span className={`${componentClassName}-highlight`}>{highlight} </span>}
          {title}
        </h3>
        {(publicationTime || sectionName) && (
          <TeaserFooter footerVariant='informationText' publicationTime={publicationTime} sectionName={sectionName} />
        )}
      </div>
      {media && (
        <div className={`${componentClassName}-img`}>
          {media}
          {showPlayButton && <IconUse iconName='play_arrow-filled' classNames={`${componentClassName}-video-icon`} />}
        </div>
      )}
    </a>
  )
}
